import React, { useEffect, useState } from "react";
import { Card, Table, Spin, Alert, Typography, Tag, Row, Col, Empty, Pagination } from "antd";
import { CreditCardOutlined, BankOutlined, DollarOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  fetchPayments,
  Payment,
  fetchPaymentMethods,
  PaymentMethod,
  fetchBanks,
  Bank,
} from "../../../api/payments.api";

const { Title, Text } = Typography;

const paymentMethodLabels: Record<string,string> = {
  cash: "Efectivo",
  card: "Tarjeta",
  transfer: "Transferencia",
  check: "Cheque",
  remittance: "Remesa",
  other: "Otro",
};

const paymentMethodIcons: Record<string, React.ReactNode> = {
  cash: <DollarOutlined />,
  card: <CreditCardOutlined />,
  transfer: <BankOutlined />,
  check: <BankOutlined />,
  remittance: <BankOutlined />,
  other: <DollarOutlined />,
};

const paymentMethodColors: Record<string, string> = {
  cash: "success",
  card: "processing",
  transfer: "warning",
  check: "default",
  remittance: "purple",
  other: "default",
};

interface Props {
  pacienteId: number;
}

const Payments: React.FC<Props> = ({ pacienteId }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string|null>(null);
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [all, mths, bks] = await Promise.all([
          fetchPayments(),
          fetchPaymentMethods(),
          fetchBanks(),
        ]);
        setMethods(mths);
        setBanks(bks);
        setPayments(all.filter(p => p.patientId === pacienteId));
        // Resetear paginación cuando cambie el paciente
        setCurrentPage(1);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Error cargando pagos");
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [pacienteId]);

  if (loading) {
    return (
      <div className="payments-container">
        <Card>
          <div style={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            minHeight: "200px" 
          }}>
            <Spin size="large" tip="Cargando pagos..." />
          </div>
        </Card>
      </div>
    );
  }
  
  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  const getPaymentMethodInfo = (methodId: number) => {
    const method = methods.find(m => m.paymentMethodId === methodId);
    if (!method) return { label: "-", icon: null, color: "default" };
    
    const label = paymentMethodLabels[method.type] || method.type;
    const icon = paymentMethodIcons[method.type];
    const color = paymentMethodColors[method.type];
    
    return { label, icon, color };
  };

  const getBankName = (bankId?: number) => {
    if (!bankId) return "-";
    const bank = banks.find(b => b.bankId === bankId);
    return bank?.name || "-";
  };

  // Funciones para manejar la paginación
  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
  };

  // Calcular datos paginados para vista móvil
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPayments = payments.slice(startIndex, endIndex);

  const cols: ColumnsType<Payment> = [
    {
      title: "Fecha",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: d => (
        <Text>{new Date(d).toLocaleDateString('es-ES')}</Text>
      ),
    },
    {
      title: "Monto",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: a => (
        <Text strong style={{ color: "#52c41a", fontSize: "1.1em" }}>
          ${a.toFixed(2)}
        </Text>
      ),
    },
    {
      title: "Método",
      dataIndex: "paymentMethodId",
      key: "method",
      width: 140,
      render: id => {
        const { label, icon, color } = getPaymentMethodInfo(id);
        return (
          <Tag icon={icon} color={color}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Banco",
      dataIndex: "bankId",
      key: "bank",
      width: 150,
      render: id => (
        <Text>{getBankName(id)}</Text>
      ),
      responsive: ['md']
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: status => (
        <Tag color={status === "completed" ? "success" : status === "pending" ? "warning" : "default"}>
          {status}
        </Tag>
      ),
    },
  ];

  return (
    <div className="payments-container">
      <Card 
        bordered={false}
        style={{ 
          borderRadius: "8px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)"
        }}
      >
        <div className="payments-header">
          <Title level={3}>Historial de Pagos</Title>
        </div>

        {payments.length === 0 ? (
          <Empty 
            description="No hay pagos registrados para este paciente"
            style={{ margin: "40px 0" }}
          />
        ) : (
          <>
            {/* Vista móvil con cards */}
            <div className="mobile-only">
              <Row gutter={[16, 16]}>
                {paginatedPayments.map((payment) => {
                  const { label, icon, color } = getPaymentMethodInfo(payment.paymentMethodId);
                  return (
                    <Col xs={24} key={payment.paymentId}>
                      <Card 
                        size="small"
                        style={{ 
                          borderLeft: `4px solid ${color === 'success' ? '#52c41a' : color === 'processing' ? '#1890ff' : '#faad14'}`,
                          backgroundColor: "#fafafa"
                        }}
                      >
                        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                          <Col>
                            <Text strong style={{ fontSize: "1.2em", color: "#52c41a" }}>
                              ${payment.amount.toFixed(2)}
                            </Text>
                          </Col>
                          <Col>
                            <Tag icon={icon} color={color}>
                              {label}
                            </Tag>
                          </Col>
                        </Row>
                        
                        <Row gutter={16}>
                          <Col span={12}>
                            <Text type="secondary">Fecha:</Text>
                            <br />
                            <Text>{new Date(payment.date).toLocaleDateString('es-ES')}</Text>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary">Estado:</Text>
                            <br />
                            <Tag color={payment.status === "completed" ? "success" : "warning"}>
                              {payment.status}
                            </Tag>
                          </Col>
                        </Row>
                        
                        {payment.bankId && (
                          <div style={{ marginTop: 12 }}>
                            <Text type="secondary">Banco:</Text>
                            <br />
                            <Text>{getBankName(payment.bankId)}</Text>
                          </div>
                        )}
                      </Card>
                    </Col>
                  );
                })}
              </Row>
              
              {/* Paginación para vista móvil */}
              {payments.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  marginTop: 24,
                  padding: '16px 0'
                }}>
                  <Pagination
                    current={currentPage}
                    total={payments.length}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                    showSizeChanger
                    showQuickJumper={false}
                    showTotal={(total, range) => 
                      `${range[0]}-${range[1]} de ${total} pagos`
                    }
                    size="small"
                    responsive
                  />
                </div>
              )}
            </div>

            {/* Vista desktop con tabla */}
            <div className="desktop-only">
              <Table<Payment>
                dataSource={payments}
                columns={cols}
                rowKey="paymentId"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: payments.length,
                  onChange: handlePageChange,
                  onShowSizeChange: handlePageChange,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} de ${total} pagos`,
                  size: "default",
                  responsive: true
                }}
                scroll={{ x: 800 }}
                size="middle"
                style={{ 
                  backgroundColor: "white",
                  borderRadius: "8px"
                }}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default Payments;